/**
 * Shared loading / error / unconfigured states for Cashflow V2 pages.
 *
 * These keep the failure UX actionable (a Retry button and a link to the
 * admin configuration page) rather than the previous "please refresh"
 * dead-end, and classify errors from the `/api/cashflow-v2/*` stack so the
 * right guidance is shown.
 */

import { AlertTriangle, RefreshCw, Settings } from 'lucide-react'
import { Link } from 'react-router-dom'

export function CashflowV2Loading({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center h-96">
      <div className="flex flex-col items-center gap-3 text-gray-500 dark:text-gray-400">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-teal-500 border-t-transparent" />
        {label ? <span className="text-sm">{label}</span> : null}
      </div>
    </div>
  )
}

export function CashflowV2Unconfigured({
  title = 'Cashflow V2 data source not configured',
  description = 'Select a Redshift connector to start loading forecast, liquidity and portfolio data.',
}: {
  title?: string
  description?: string
}) {
  return (
    <div className="flex items-center justify-center h-96">
      <div className="max-w-md text-center bg-white dark:bg-dark-surface border border-dashed border-gray-300 dark:border-dark-border rounded-lg p-8">
        <Settings className="mx-auto h-10 w-10 text-teal-500" />
        <h2 className="mt-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {title}
        </h2>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{description}</p>
        <Link
          to="/cashflow-v2/admin/configuration"
          className="inline-flex items-center gap-2 mt-6 px-4 py-2 rounded-md bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium"
        >
          <Settings className="h-4 w-4" />
          Open configuration
        </Link>
      </div>
    </div>
  )
}

/**
 * Heuristic: does an error returned by TanStack Query look like "data source
 * is not configured" vs. an actual backend failure? We check for 503 + well
 * known server phrases. Defensive: any unexpected error still renders the
 * connection-failure panel so users get a useful retry affordance.
 */
export function looksUnconfigured(err: unknown): boolean {
  if (!err) return false
  const anyErr = err as {
    status?: number
    response?: { status?: number; data?: { detail?: string; message?: string } }
    message?: string
  }
  const status = anyErr.response?.status ?? anyErr.status
  const detail =
    anyErr.response?.data?.detail ||
    anyErr.response?.data?.message ||
    anyErr.message ||
    ''
  if (status === 503) return true
  return /not configured|not set up|data source/i.test(detail)
}

export function CashflowV2Error({
  title = 'Could not load dashboard data',
  description,
  onRetry,
}: {
  title?: string
  description?: string
  onRetry?: () => void
}) {
  return (
    <div className="flex items-center justify-center h-96">
      <div className="max-w-md text-center bg-white dark:bg-dark-surface border border-red-200 dark:border-red-900/30 rounded-lg p-8">
        <AlertTriangle className="mx-auto h-10 w-10 text-red-500" />
        <h2 className="mt-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {title}
        </h2>
        {description ? (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 break-words">
            {description}
          </p>
        ) : null}
        <div className="mt-6 flex items-center justify-center gap-2">
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium"
            >
              <RefreshCw className="h-4 w-4" />
              Retry
            </button>
          ) : null}
          <Link
            to="/cashflow-v2/admin/configuration"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gray-300 dark:border-dark-border text-gray-700 dark:text-gray-200 text-sm"
          >
            <Settings className="h-4 w-4" />
            Check configuration
          </Link>
        </div>
      </div>
    </div>
  )
}
